import { Component, Input, OnChanges, OnInit, SimpleChanges, inject, signal } from '@angular/core';
import { takeUntil } from 'rxjs/operators';

import { LinkLogin, LoginProviders, User } from '@shared/models/user.model';
import { Friend, Friendship } from '@app/home/friends/friends.model';
import { FriendsService } from '@app/home/friends/friends.service';
import { RealtimeService } from '@shared/services/realtime.service';
import { UserService } from '@shared/services/user.service';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import { EventManager } from '@app/shared/utils/event-manager';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { LinkLoginService } from '@shared/services/linkLogin.service';
import { NativeService } from '@shared/services/native.service';
import { Observable } from 'rxjs';
import { SharedModule } from '@app/shared/shared.module';
import { AvatarComponent } from '@app/shared/components/avatar/avatar.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { ParagraphComponent } from '@app/shared/components/text/p/p.component';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-profile-header',
  templateUrl: './profile-header.component.html',
  styleUrls: ['./profile-header.component.scss'],
  standalone: true,
  imports: [SharedModule, AvatarComponent, ButtonComponent, ParagraphComponent],
})
export class ProfileHeaderComponent implements OnInit, OnChanges {
  @Input() currentUser: User;
  @Input() profileUser: User;
  @Input() isSelf: boolean;
  showNotification = signal(false);
  linkLogins = signal(false);
  useAppleId = signal(false);
  useGooglePlay = signal(false);

  friendship = signal<Friendship | undefined>(undefined);
  componentDestroy: () => Observable<unknown>;

  private readonly friendsService = inject(FriendsService);
  private readonly userService = inject(UserService);
  private readonly realtimeService = inject(RealtimeService);
  public readonly logger = inject(LoggerService);
  private readonly linkLoginService = inject(LinkLoginService);
  private readonly eventManager = inject(EventManager);
  private readonly nativeService = inject(NativeService);

  ngOnInit() {
    this.realtimeService
      .on('SendFriendRequest')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (data: any) => {
          if (this.profileUser.id !== data.pendingFriend.id) return;
          this.setFriendship(Friendship.PendingFriend);
        },
        error: (error) => this.logger.error(error),
      });

    this.realtimeService
      .on('CancelFriendRequest')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (data: any) => {
          if (this.profileUser.id !== data.pendingFriendId) return;
          this.setFriendship(Friendship.Unfriend);
        },
        error: (error) => this.logger.error(error),
      });

    this.realtimeService
      .on('AcceptFriendRequest')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (data: any) => {
          if (this.profileUser.id !== data.friend.id) return;
          this.setFriendship(Friendship.Friend);
        },
        error: (error) => this.logger.error(error),
      });

    this.realtimeService
      .on('RejectFriendRequest')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (data: any) => {
          if (this.profileUser.id !== data.pendingFriendId) return;
          this.setFriendship(Friendship.Unfriend);
        },
        error: (error) => this.logger.error(error),
      });

    this.realtimeService
      .on('Unfriend')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (data: any) => {
          if (this.profileUser.id !== data.friendId) return;
          this.setFriendship(Friendship.Unfriend);
        },
        error: (error) => this.logger.error(error),
      });

    this.linkLoginService.linkLoginError$.pipe(takeUntil(this.componentDestroy())).subscribe({
      next: (linkLogin: LinkLogin) => {
        this.eventManager.openModal(
          new Modal(
            NonPersistedModalName.LoginErrorModalComponent,
            ModalType.View,
            {
              platform: linkLogin.loginProvider,
              errorCode: linkLogin.errorCode,
            },
            'login-error-dialog'
          )
        );
      },
      error: (error) => this.logger.error(error),
    });

    if (this.currentUser.hasPlayed && !this.currentUser.hideNotificationChangeNickName) {
      this.currentUser.hideNotificationChangeNickName = true;
      this.userService
        .updateUser(this.currentUser)
        .pipe(takeUntil(this.componentDestroy()))
        .subscribe({
          next: () => {
            this.showNotification.set(true);
          },
          error: (error) => this.logger.error(error),
        });
    }

    this.linkLogins.set(true);
    this.useAppleId.set(this.nativeService.isNativeIos());
    this.useGooglePlay.set(
      this.nativeService.isNativeAndroid() && !this.nativeService.isCurrentVersionLowerThanInput('1.36.0')
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.profileUser || !changes.profileUser.currentValue) return;
    if (!this.isSelf) {
      this.getFriendship();
    }
  }

  sendFriendRequest(): void {
    this.friendsService
      .sendFriendRequest(this.profileUser.id)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: () => this.setFriendship(Friendship.PendingRequest),
        error: (error) => this.logger.error(error),
      });
  }

  cancelFriendRequest(): void {
    this.friendsService
      .cancelFriendRequest(this.profileUser.id)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: () => this.setFriendship(Friendship.Unfriend),
        error: (error) => this.logger.error(error),
      });
  }

  acceptFriendRequest(): void {
    this.friendsService
      .acceptFriendRequest(this.profileUser.id)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (friend: Friend) => {
          const currentUser = {
            id: this.currentUser.id,
            userName: this.currentUser.userName,
            nickName: this.currentUser.nickName,
            friendship: Friendship.Friend,
          };
          this.friendsService.notifyAcceptFriend(currentUser);
          this.friendsService.notifyAcceptFriend(friend);
          this.setFriendship(Friendship.Friend);
        },
        error: (error) => this.logger.error(error),
      });
  }

  rejectFriendRequest(): void {
    this.friendsService
      .rejectFriendRequest(this.profileUser.id)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (userId: string) => {
          this.friendsService.notifyRejectFriend(userId);
          this.setFriendship(Friendship.Unfriend);
        },
        error: (error) => this.logger.error(error),
      });
  }

  unfriend(): void {
    this.friendsService
      .unfriend(this.profileUser.id)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (userId: string) => {
          this.friendsService.notifyUnfriend(userId);
          this.friendsService.notifyUnfriend(this.profileUser.id);
          this.setFriendship(Friendship.Unfriend);
        },
        error: (error) => this.logger.error(error),
      });
  }

  private getFriendship(): void {
    this.friendsService
      .getFriendship(this.profileUser.id)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (friendship: Friendship) => this.setFriendship(friendship),
        error: (error) => this.logger.error(error),
      });
  }

  private setFriendship(friendship: Friendship): void {
    this.friendship.set(friendship);
  }

  linkLogin(loginProvider: LoginProviders): void {
    this.linkLoginService.link(loginProvider);
  }

  manageName() {
    this.eventManager.openModal(new Modal(NonPersistedModalName.ManageNameComponent, ModalType.View, {}, 'default'));
  }

  manageProfile() {
    this.eventManager.openModal(new Modal(NonPersistedModalName.ManageProfileComponent, ModalType.View, {}, 'default'));
  }

  getGenderKey(): string {
    return this.profileUser.gender
      ? 'home.profile.' + this.profileUser.gender.toLowerCase()
      : 'home.profile.undisclosed';
  }
}
